import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { INTRO_FLIP_MS, presenterFlip, type AvatarIntroMedia, type PresenterBox } from './websiteAgentLayout';

/**
 * What the RESTING launcher tile plays when the avatar has an approach intro.
 *
 * The presenter stands a few steps back in a looping far idle clip. Now and
 * then one far-pose gesture plays once and returns to the loop; and when the
 * tile comes back after a call it opens with the retreat — the approach
 * reversed — so the presenter is seen walking back to where they wait.
 *
 * Every clip here starts and ends on the far pose (the retreat starts on the
 * close pose and ends on the far one), so each hand-over is the same 220ms
 * cross-fade `AvatarVideo` already does between any two sources.
 *
 * NOTHING HERE WAITS ON THE NETWORK. A gesture or a retreat that has not
 * finished preloading is skipped (`isReady`), because a filler that stalls the
 * presenter is worse than no filler. Fillers are garnish; the far idle loop is
 * the only clip this state ever needs.
 */

/** Rest before the first gesture: long enough that the tile has settled in. */
export const FIRST_GESTURE_DELAY_MS = 4_000;
/** Idle time between one gesture ending and the next starting. */
export const GESTURE_GAP_MS = 9_000;

export type RestClip =
  | { kind: 'idle' }
  | { kind: 'gesture'; index: number }
  | { kind: 'retreat' };

export interface RestSource {
  src: string;
  poster: string;
  loop: boolean;
}

/** The media one rest clip plays. `closePosterUrl` backs the retreat's first frame. */
export function restSource(
  clip: RestClip,
  intro: AvatarIntroMedia,
  closePosterUrl: string | undefined,
): RestSource {
  if (clip.kind === 'gesture') {
    const src = intro.gestureUrls[clip.index];
    // An index that fell out of range (config changed under us) rests instead.
    if (src) return { src, poster: intro.farPosterUrl, loop: false };
  }
  if (clip.kind === 'retreat' && intro.retreatUrl) {
    // The retreat OPENS on the close pose, so the still under it must be the
    // close poster: the far one would flash the wrong pose before frame one.
    return { src: intro.retreatUrl, poster: closePosterUrl || intro.farPosterUrl, loop: false };
  }
  return { src: intro.farIdleLoopUrl, poster: intro.farPosterUrl, loop: true };
}

/**
 * The next gesture to play after `lastIndex`: the first READY one in cycle
 * order, or -1 when none is. Cycling (rather than random) means a visitor who
 * lingers sees every gesture before any repeats.
 */
export function nextReadyGesture(
  gestureUrls: readonly string[],
  lastIndex: number,
  isReady: (url: string) => boolean,
): number {
  const count = gestureUrls.length;
  for (let step = 1; step <= count; step += 1) {
    const index = (lastIndex + step) % count;
    if (isReady(gestureUrls[index])) return index;
  }
  return -1;
}

export interface AvatarRestSequenceOptions {
  /** The usable intro, or undefined to disable the sequence entirely. */
  intro: AvatarIntroMedia | undefined;
  closePosterUrl: string | undefined;
  /** False while the tile is not the visible resting surface. */
  active: boolean;
  /** The tile has just come back from a call: open with the retreat. */
  playRetreat: boolean;
  /** Tell the owner the retreat has been consumed (played, or skipped). */
  onRetreatConsumed: () => void;
  isReady: (url: string) => boolean;
}

export interface AvatarRestSequence extends RestSource {
  clip: RestClip;
  /** Wire to `AvatarVideo.onEnded`: a one-shot clip returns to the far loop. */
  onEnded: () => void;
}

const pageHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

export function useAvatarRestSequence(options: AvatarRestSequenceOptions): AvatarRestSequence | null {
  const { intro, closePosterUrl, active, playRetreat, onRetreatConsumed, isReady } = options;
  const isReadyRef = useRef(isReady);
  isReadyRef.current = isReady;
  const onRetreatConsumedRef = useRef(onRetreatConsumed);
  onRetreatConsumedRef.current = onRetreatConsumed;

  // Decided ONCE, at mount: the retreat is the tile's opening clip or it is
  // nothing. Deciding it in an effect would paint the far loop for a frame and
  // then cut to the close pose — the jump the retreat exists to avoid.
  const [clip, setClip] = useState<RestClip>(() =>
    playRetreat && intro?.retreatUrl && isReady(intro.retreatUrl) ? { kind: 'retreat' } : { kind: 'idle' },
  );
  const lastGestureRef = useRef(-1);

  useEffect(() => {
    // Played or skipped, the request is spent: a later remount of the tile (a
    // resize across tiers, say) must not replay a walk-back from minutes ago.
    if (playRetreat) onRetreatConsumedRef.current();
  }, [playRetreat]);

  const gestureCount = intro?.gestureUrls.length ?? 0;
  const gestureUrls = intro?.gestureUrls;
  useEffect(() => {
    if (!active || clip.kind !== 'idle' || !gestureUrls || gestureCount === 0) return;
    let timer: ReturnType<typeof setTimeout>;
    const arm = (delay: number) => {
      timer = setTimeout(() => {
        // A gesture nobody can see is a decode for nothing, and a backgrounded
        // tab throttles video anyway. Look again after the ordinary gap.
        if (pageHidden()) return arm(GESTURE_GAP_MS);
        const index = nextReadyGesture(gestureUrls, lastGestureRef.current, isReadyRef.current);
        // None ready YET — the fillers load last, in idle time. Keep resting.
        if (index < 0) return arm(GESTURE_GAP_MS);
        lastGestureRef.current = index;
        setClip({ kind: 'gesture', index });
      }, delay);
    };
    arm(lastGestureRef.current < 0 ? FIRST_GESTURE_DELAY_MS : GESTURE_GAP_MS);
    return () => clearTimeout(timer);
  }, [active, clip.kind, gestureUrls, gestureCount]);

  const onEnded = useCallback(() => setClip({ kind: 'idle' }), []);

  if (!intro) return null;
  return { clip, onEnded, ...restSource(clip, intro, closePosterUrl) };
}

/**
 * Carry the presenter from the box it was just drawn in to the one it is drawn
 * in now, over the clip that is playing (see {@link presenterFlip}).
 *
 * Returns a ref for the element that holds the presenter. On mount — and only
 * then — it measures that element, starts it at the OLD geometry and animates
 * to its own over `durationMs` (the retreat's travel time unless told otherwise). The Web Animations API is used rather than a style + transition
 * pair: it needs no forced reflow, leaves React's `style` prop alone, and a
 * browser without it simply shows the presenter at its final size.
 */
export function usePresenterFlip(
  fromBox: PresenterBox | null | undefined,
  active: boolean,
  durationMs: number = INTRO_FLIP_MS,
) {
  const elRef = useRef<HTMLElement | null>(null);
  // Mount-only by intent: the FLIP answers "where was the presenter a moment
  // ago", and that question has one answer per mount.
  const initial = useRef({ fromBox, active, durationMs });
  useLayoutEffect(() => {
    const el = elRef.current;
    const { fromBox: from, active: on, durationMs: duration } = initial.current;
    if (!on || !from || !el || typeof el.animate !== 'function') return;
    const rect = el.getBoundingClientRect();
    const flip = presenterFlip(from, { left: rect.left, top: rect.top, width: rect.width, height: rect.height });
    if (!flip) return;
    const previousOrigin = el.style.transformOrigin;
    // `cancel()` only QUEUES its event. Under a StrictMode remount that queued
    // restore would land in the middle of the second run's animation and move
    // the origin off the presenter's feet — so a run that has been cleaned up
    // restores synchronously and ignores its own late events.
    let live = true;
    el.style.transformOrigin = `${flip.originX}px ${flip.originY}px`;
    const animation = el.animate(
      [{ transform: `translate(${flip.dx}px, ${flip.dy}px) scale(${flip.scale})` }, { transform: 'none' }],
      // Eases OUT: most of the travel happens while the clip is mid-stride, and
      // the box is all but still by the time the presenter settles.
      { duration, easing: 'cubic-bezier(0.33, 0, 0.2, 1)' },
    );
    const restore = () => {
      el.style.transformOrigin = previousOrigin;
    };
    animation.onfinish = () => {
      if (live) restore();
    };
    return () => {
      live = false;
      animation.cancel();
      restore();
    };
  }, []);
  return elRef;
}
